import {
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  AmbientLight,
  PointLight,
  Color,
  GridHelper,
  EquirectangularReflectionMapping,
  LoadingManager,
  Box3,
  Vector3
} from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { HDRLoader } from "three/examples/jsm/loaders/HDRLoader";
import { Loader } from "./loaders/assets/loader";
import { ModelLoadSource } from "./utils/general.utils";
import { scaleMeshToMatchHeight } from "./utils/geometry.utils";

const MODEL_VIEWER_HDRI_URL = (
  process.env.REACT_APP_MODEL_VIEWER_HDRI_URL ||
  "https://dev-artetra-files.s3.eu-west-1.amazonaws.com/gallery-hdri/monkstown_castle_1k.hdr"
).trim();

type ModelViewerInitOptions = {
  onError?: (error: unknown) => void;
};

type ModelViewerFraming = {
  center: Vector3;
  radius: number;
  position: Vector3;
  near: number;
  far: number;
  minDistance: number;
  maxDistance: number;
};

export const computeModelViewerFraming = (bounds: Box3, fovDegrees: number): ModelViewerFraming | null => {
  const center = bounds.getCenter(new Vector3());
  const size = bounds.getSize(new Vector3());
  const radius = size.length() * 0.5;
  const maxDimension = Math.max(size.x, size.y, size.z);

  if (!Number.isFinite(radius) || radius <= 0 || !Number.isFinite(maxDimension) || maxDimension <= 0) {
    return null;
  }

  const halfFov = (fovDegrees * Math.PI) / 360;
  const fitByHeightDistance = radius / Math.sin(halfFov);
  const fitByWidthDistance = (maxDimension * 0.5) / Math.tan(halfFov);
  const distance = Math.max(fitByHeightDistance, fitByWidthDistance) * 1.75;
  const direction = new Vector3(1, 0.6, 1).normalize();
  const position = center.clone().add(direction.multiplyScalar(distance));
  const near = Math.max(0.01, distance / 100);
  const far = Math.max(distance + radius * 8, near + 1);
  const minDistance = Math.max(0.05, radius * 0.02);
  const maxDistance = Math.max(distance * 4.5, minDistance + radius * 2, minDistance + 1);

  return {
    center,
    radius,
    position,
    near,
    far,
    minDistance,
    maxDistance,
  };
};

export class ModelViewer {
  private scene: Scene;
  private camera: PerspectiveCamera;
  private renderer: WebGLRenderer;
  private controls: OrbitControls;
  private ambientLight: AmbientLight;
  private pointLight1: PointLight;
  private pointLight2: PointLight;
  private container: HTMLElement;
  private loader: Loader;
  private hdrLoader: HDRLoader;
  private loadingManager: LoadingManager;
  private loadingText: HTMLDivElement | null = null;
  private resizeHandler: (() => void) | null = null;
  private animationFrameId: number | null = null;
  private isDisposed = false;
  private suppressShaderDiagnosticsForNextRender = false;

  public init = async (
    div: HTMLElement,
    modelSource: ModelLoadSource,
    targetHeightCmOrOptions?: number | ModelViewerInitOptions,
    options?: ModelViewerInitOptions
  ) => {
    const targetHeightCm = typeof targetHeightCmOrOptions === "number" ? targetHeightCmOrOptions : undefined;
    const initOptions =
      typeof targetHeightCmOrOptions === "object" && targetHeightCmOrOptions !== null
        ? targetHeightCmOrOptions
        : options;

    if (!modelSource || (typeof modelSource === "string" && modelSource === "")) {
      return;
    }

    // Clean up existing viewer if it exists
    if (this.scene) {
      this.dispose();
    }

    this.isDisposed = false;
    this.container = div;
    div.innerHTML = "";
    if (!div.style.position) {
      div.style.position = "relative";
    }
    div.style.background = "#ffffff";
    div.style.overflow = "hidden";
    div.style.lineHeight = "0";

    // Create loading manager
    this.loadingManager = new LoadingManager();
    this.hdrLoader = new HDRLoader(this.loadingManager);
    this.loader = new Loader();

    // Create scene
    this.scene = new Scene();
    this.scene.background = new Color(0xffffff);

    // Create camera
    this.camera = new PerspectiveCamera(
      55,
      div.clientWidth / div.clientHeight,
      0.1,
      1000
    );
    this.camera.position.set(0, 1, 1);

    // Create renderer
    this.renderer = new WebGLRenderer({ antialias: true });
    this.renderer.setClearColor(0xffffff);
    this.renderer.setSize(div.clientWidth, div.clientHeight);
    this.renderer.domElement.style.display = "block";
    this.renderer.domElement.style.width = "100%";
    this.renderer.domElement.style.height = "100%";
    div.appendChild(this.renderer.domElement);

    // Create orbit controls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.screenSpacePanning = false;
    this.controls.minDistance = 1;
    this.controls.maxDistance = 100;
    this.controls.target.set(0, 1, 0);

    // Add basic lighting
    this.ambientLight = new AmbientLight(0xffffff, 1);
    this.scene.add(this.ambientLight);

    this.pointLight1 = new PointLight(0xffffff, 1);
    this.pointLight1.position.set(2, 2, 2);
    this.scene.add(this.pointLight1);

    this.pointLight2 = new PointLight(0xffffff, 1);
    this.pointLight2.position.set(-2, 2, -2);
    this.scene.add(this.pointLight2);


    // Initial render
    this.addEvents(div);
    try {
      await this.load(modelSource, targetHeightCm);
    } catch (error) {
      this.showLoadingError("Error loading model...");
      initOptions?.onError?.(error);
    }
  };

  private addEvents = (div: HTMLElement) => {
    const loadingText = document.createElement('div');
    loadingText.style.position = 'absolute';
    loadingText.style.top = '50%';
    loadingText.style.left = '50%';
    loadingText.style.transform = 'translate(-50%, -50%)';
    loadingText.style.color = '#0f172a';
    loadingText.style.fontSize = '1rem';
    loadingText.style.fontWeight = '700';
    loadingText.style.background = 'rgba(255, 255, 255, 0.92)';
    loadingText.style.border = '1px solid rgba(15, 23, 42, 0.08)';
    loadingText.style.borderRadius = '999px';
    loadingText.style.boxShadow = '0 18px 36px rgba(15, 23, 42, 0.12)';
    loadingText.style.padding = '0.75rem 1rem';
    loadingText.style.lineHeight = '1.2';
    loadingText.innerHTML = 'Loading...';
    loadingText.style.display = 'block';
    div.appendChild(loadingText);
    this.loadingText = loadingText;

    // Handle window resize
    this.resizeHandler = () => {
      if (this.isDisposed || !this.camera || !this.renderer) {
        return;
      }
      this.camera.aspect = div.clientWidth / div.clientHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(div.clientWidth, div.clientHeight);
    };

    window.addEventListener('resize', this.resizeHandler);
  }

  private setLoadingMessage = (message: string) => {
    if (!this.loadingText) return;
    this.loadingText.style.display = 'block';
    this.loadingText.innerHTML = message;
  }

  private hideLoadingMessage = () => {
    if (!this.loadingText) return;
    this.loadingText.style.display = 'none';
  }

  private showLoadingError = (message: string) => {
    if (!this.loadingText) return;
    this.loadingText.style.display = 'block';
    this.loadingText.innerHTML = message;
  }

  private load = async (modelSource: ModelLoadSource, targetHeightCm?: number) => {
    this.setLoadingMessage("Loading...");
    await Promise.all([this.loadEnvironment(), this.loadModel(modelSource, targetHeightCm)]);
    if (this.isDisposed) {
      return;
    }

    // const gridHelper = new GridHelper(10, 10, 0x666666, 0x222222);
    // gridHelper.position.y = 0;
    // this.scene.add(gridHelper);

    this.hideLoadingMessage();
    this.suppressShaderDiagnosticsForNextRender = true;
    this.render();
  }

  private loadEnvironment = () => {
    if (!MODEL_VIEWER_HDRI_URL) {
      return Promise.resolve(false);
    }

    return new Promise((resolve) => {
      this.hdrLoader.load(MODEL_VIEWER_HDRI_URL, (texture) => {
        if (this.isDisposed) {
          texture.dispose();
          resolve(false);
          return;
        }
        texture.mapping = EquirectangularReflectionMapping;
        this.scene.environment = texture;
        resolve(true);
      }, undefined, (error) => {
        console.warn("ModelViewer: Failed to load environment map", {
          hdriUrl: MODEL_VIEWER_HDRI_URL,
          error
        });
        resolve(false);
      });
    });
  }

  private loadModel = (modelSource: ModelLoadSource, targetHeightCm?: number) => {
    return this.loader.load3DModel(modelSource, false, this.renderer)?.then((model: any) => {
      if (!model) return null;
      this.scene.add(model);

      if (targetHeightCm && Number.isFinite(targetHeightCm) && targetHeightCm > 0) {
        scaleMeshToMatchHeight(model, targetHeightCm * 0.01);
      }

      const boundingBox = this.computeSafeModelBounds(model);
      if (!boundingBox) {
        console.warn("ModelViewer: Unable to compute valid bounds for loaded model", { modelSource });
        this.controls.update();
        return model;
      }

      const framing = computeModelViewerFraming(boundingBox, this.camera.fov);
      if (!framing) {
        console.warn("ModelViewer: Loaded model has invalid dimensions", { modelSource, bounds: boundingBox });
        this.controls.update();
        return model;
      }

      this.camera.position.copy(framing.position);
      this.camera.near = framing.near;
      this.camera.far = framing.far;
      this.camera.lookAt(framing.center);
      this.camera.updateProjectionMatrix();
      this.controls.target.copy(framing.center);
      this.controls.minDistance = framing.minDistance;
      this.controls.maxDistance = framing.maxDistance;
      this.controls.update();
      return model;
    }) || null
  }

  private geometryHasFinitePositions = (geometry: any) => {
    const position = geometry?.getAttribute?.("position");
    const array = position?.array;
    if (!array || typeof array.length !== "number" || array.length === 0) {
      return false;
    }

    for (let index = 0; index < array.length; index++) {
      if (!Number.isFinite(array[index])) {
        return false;
      }
    }

    return true;
  }

  private computeSafeModelBounds = (model: any) => {
    const bounds = new Box3();
    let hasBounds = false;

    model?.updateMatrixWorld?.(true);
    model?.traverse?.((child: any) => {
      if (!(child instanceof Scene) && !child?.isMesh) {
        return;
      }

      const geometry = child.geometry;
      if (!geometry || !this.geometryHasFinitePositions(geometry)) {
        if (geometry) {
          console.warn("ModelViewer: Skipping mesh with invalid geometry positions", {
            name: child.name,
            type: child.type
          });
        }
        return;
      }

      geometry.computeBoundingBox();
      const localBoundingBox = geometry.boundingBox;
      if (!localBoundingBox) return;

      const worldBoundingBox = localBoundingBox.clone().applyMatrix4(child.matrixWorld);
      if (
        !Number.isFinite(worldBoundingBox.min.x) ||
        !Number.isFinite(worldBoundingBox.min.y) ||
        !Number.isFinite(worldBoundingBox.min.z) ||
        !Number.isFinite(worldBoundingBox.max.x) ||
        !Number.isFinite(worldBoundingBox.max.y) ||
        !Number.isFinite(worldBoundingBox.max.z)
      ) {
        return;
      }

      bounds.union(worldBoundingBox);
      hasBounds = true;
    });

    return hasBounds ? bounds : null;
  }



  private renderSceneWithDiagnosticsState() {
    const debugState = (this.renderer as any)?.debug;
    if (!this.suppressShaderDiagnosticsForNextRender || !debugState || typeof debugState.checkShaderErrors !== "boolean") {
      this.renderer.render(this.scene, this.camera);
      return;
    }

    const previousCheckShaderErrors = debugState.checkShaderErrors;
    debugState.checkShaderErrors = false;
    try {
      this.renderer.render(this.scene, this.camera);
    } finally {
      debugState.checkShaderErrors = previousCheckShaderErrors;
      this.suppressShaderDiagnosticsForNextRender = false;
    }
  }

  private render = () => {
    if (this.isDisposed || !this.controls || !this.renderer || !this.scene || !this.camera) {
      return;
    }
    this.controls.update();
    this.renderSceneWithDiagnosticsState();
    this.animationFrameId = requestAnimationFrame(this.render);
  }

  public dispose = () => {
    this.isDisposed = true;
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }
    if (this.loadingText && this.loadingText.parentNode) {
      this.loadingText.parentNode.removeChild(this.loadingText);
    }
    this.loadingText = null;
    // Dispose of previous scene, renderer, and controls if they exist
    if (this.renderer) {
      this.renderer.dispose();
      if (this.renderer.domElement && this.renderer.domElement.parentNode) {
        this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
      }
      this.renderer = null;
    }
    if (this.scene) {
      // Traverse and dispose all geometries and materials
      this.scene.traverse((object: any) => {
        if (object.geometry) {
          object.geometry.dispose();
        }
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach((mat: any) => {
              if (mat.dispose) mat.dispose();
            });
          } else if (object.material.dispose) {
            object.material.dispose();
          }
        }
        // Texture disposal (if any)
        if (object.material && object.material.map && object.material.map.dispose) {
          object.material.map.dispose();
        }
      });
      // Remove all children from the scene
      while (this.scene.children.length > 0) {
        this.scene.remove(this.scene.children[0]);
      }
      const sceneEnvironment = this.scene.environment as any;
      if (sceneEnvironment && typeof sceneEnvironment.dispose === "function") {
        sceneEnvironment.dispose();
      }
      this.scene = null;
    }
    if (this.controls) {
      this.controls.dispose();
      this.controls = null;
    }
    if (this.ambientLight) {
      this.ambientLight = null;
    }
    if (this.pointLight1) {
      this.pointLight1 = null;
    }
    if (this.pointLight2) {
      this.pointLight2 = null;
    }
    if (this.loader) {
      this.loader.dispose();
      this.loader = null;
    }
    if (this.hdrLoader) {
      this.hdrLoader = null;
    }
    if (this.loadingManager) {
      this.loadingManager = null;
    }
    if (this.container) {
      this.container = null;
    }

    this.camera = null;
    this.ambientLight = null;
    this.pointLight1 = null;
    this.pointLight2 = null;
    this.loadingManager = null;
  };

  public getScene = () => this.scene;
  public getCamera = () => this.camera;
  public getRenderer = () => this.renderer;
  public getControls = () => this.controls;
  public getLoadingManager = () => this.loadingManager;
}

export default ModelViewer; 
